<template>
  <div
    class="px-2 animation-slide"
    v-if="!isLoading && tableData && tableData.length > 0"
  >
    <div class="d-flex align-items-center justify-content-start total-records">
      Total Records: <b>{{ totalElements }}</b>
    </div>
    <div class="overflow-x-auto z-95">
      <table class="min-w-full table-auto">
        <thead>
          <tr class="text-white">
            <th
              v-for="column in columns"
              :key="column.slug"
              class="py-2 text-left cursor-pointer select-none bg-transparent"
              :id="`table-th-${column.slug}`"
            >
              <span
                class="d-flex align-items-center justify-content-start gap-2"
              >
                {{ column.label || column.slug }}
                <span
                  class="text-white d-flex align-items-center justify-content-start"
                  v-if="column.sortable"
                >
                  <i
                    @click="
                      column.sortable ? toggleSort(column.slug, 'desc') : null
                    "
                    class="bi bi-arrow-up-short sort-arrow"
                    :class="{ highlight: isSortActive(column.slug, 'desc') }"
                  ></i>
                  <i
                    @click="
                      column.sortable ? toggleSort(column.slug, 'asc') : null
                    "
                    class="bi bi-arrow-down-short sort-arrow"
                    :class="{ highlight: isSortActive(column.slug, 'asc') }"
                  ></i>
                </span>
                <span v-else-if="column.sortable" class="text-muted">
                  <i class="bi bi-arrow-down-up"></i>
                </span>
              </span>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(row, rowIndex) in tableData"
            :key="rowIndex"
            class="border-b border-gray-200"
            :class="rowIndex % 2 === 0 ? 'odd-row' : 'even-row'"
          >
            <td
              :class="`table-${column.slug}`"
              v-for="(column, index) in columns"
              :key="column.slug"
              class=""
              :id="`table-td-${column.slug}`"
            >
              <div
                v-if="allowMultipleSelection && index == 0"
                class="multiple-selection"
              >
                <div
                  class="custom-checkbox cursor-pointer"
                  :class="checkIsSelected(row) ? 'checked' : 'not-checked'"
                  @click="multipleSelection(row)"
                ></div>
              </div>
              <template v-if="column.type === 'date'">
                <span v-if="column.slug !== 'cashout_time'" class="date-cell">{{
                  formatDate(row[column.slug])
                }}</span>
                <span v-else class="date-cell">{{
                  formatDate(row["updated_at"])
                }}</span>
              </template>
              <template v-if="column.type === 'time'">
                <span class="date-cell">{{
                  formatTime(row[column.slug])
                }}</span>
              </template>
              <template v-if="column.type === 'boolean'">
                <span
                  class="d-flex align-items-center"
                  :class="{
                    'text-green-500': row[column.slug],
                    'text-red-500': !row[column.slug],
                  }"
                >
                  <i
                    class="bi bi-patch-check-fill text-success"
                    v-if="row[column.slug]"
                  ></i>
                  <i v-else class="bi bi-slash-circle-fill text-danger"></i>

                  <svg
                    v-if="
                      column.slug === 'is_closed' &&
                      showRestoreButton &&
                      row[column.slug] &&
                      !checkIfIsEnd(row)
                    "
                    @click="restoreFreebets(row)"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  >
                    <path
                      d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8"
                    ></path>
                    <path d="M21 3v5h-5"></path>
                    <path
                      d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16"
                    ></path>
                    <path d="M3 21v-5h5"></path>
                  </svg>
                  <template
                    v-if="
                      column.text &&
                      column.textType.slug === column.slug &&
                      column.textType.valueToShow === row[column.slug]
                    "
                  >
                    <button
                      @click="handleButtonClick(row, column.textType)"
                      class="px-2 w-100 d-flex align-items-center justify-content-end text-end"
                    >
                      <p :class="column.textType.customClass" class="mb-0">
                        {{ column.textType.label }}
                      </p>
                    </button>
                  </template>
                </span>
              </template>
              <template v-if="column.type === 'button'">
                <button
                  v-if="
                    column.button && row[column.slug] && row[column.slug] !== ''
                  "
                  @click="handleButtonClick(row, column.buttonType)"
                  class=""
                >
                  <span class="me-2" v-if="column.buttonType.label">{{
                    row[column.slug]
                  }}</span>
                  <i
                    :class="`fas fa-${column.buttonType.icon} custom-icon-color`"
                  ></i>
                </button>
                <div class="text-left" v-else>--</div>
              </template>
              <template
                v-if="
                  column.type === 'action_button' &&
                  column.buttonType.action !== 'openGeolocationInfo'
                "
              >
                <button
                  v-if="column.button"
                  @click="handleButtonClick(row, column.buttonType)"
                  class=""
                >
                  <i
                    :class="`fas fa-${column.buttonType.icon} ${
                      column.buttonType.customClass ?? ''
                    } custom-icon-color`"
                  ></i>
                </button>
              </template>
              <template v-else-if="column.type === 'action_button'">
                <button
                  v-if="column.button && row?.ip && row.ip.trim() !== ''"
                  @click="handleButtonClick(row, column.buttonType)"
                  class=""
                >
                  <i
                    :class="`fas fa-${column.buttonType.icon} ${
                      column.buttonType.customClass ?? ''
                    } custom-icon-color`"
                  ></i>
                </button>
              </template>
              <template v-else-if="column.type === 'download_file'">
                <button
                  v-if="
                    column.button &&
                    row[column.buttonType.propertyToCheck] != null &&
                    row[column.buttonType.propertyToCheck] !== ''
                  "
                  @click="handleButtonClick(row, column.buttonType)"
                  class=""
                >
                  <i
                    :class="`fas fa-${column.buttonType.icon} ${
                      column.buttonType.customClass ?? ''
                    } custom-icon-color custom-icon-file-download`"
                  ></i>
                </button>
                <button v-else>N/A</button>
              </template>

              <template v-else-if="column.slug === 'amount'">
                <span>
                  {{ parseFloat(row["amount"]).toFixed(2) }}
                  {{ row["currency_code"] }} <br />
                  {{
                    isNaN(row["amount_converted"])
                      ? ""
                      : "/" +
                        parseFloat(row["amount_converted"]).toFixed(2) +
                        " EUR"
                  }}
                </span>
              </template>

              <template v-else-if="column.slug === 'autocashout'">
                <span
                  >{{
                    parseInt(row["autocashout"]) !== 0
                      ? `x ${parseFloat(row["autocashout"]).toFixed(2)}`
                      : "Not setted"
                  }}
                </span>
              </template>

              <template v-else-if="column.slug === 'win'">
                <span
                  :class="
                    parseFloat(row[column.slug]).toFixed(2) > 0
                      ? 'win-value'
                      : ''
                  "
                >
                  {{ parseFloat(row["win"]).toFixed(2) }}
                  {{ row["currency_code"] }} /<br />
                  {{ parseFloat(row["win_converted"]).toFixed(2) + " EUR" }}
                </span>
              </template>

              <template v-else-if="column.slug === 'cashedOut'">
                <span
                  :class="
                    parseFloat(row.cashedOut) > 0 && parseFloat(row.win) > 0
                      ? checkMultiplier(row[column.slug])
                      : ''
                  "
                >
                  {{
                    parseFloat(row.cashedOut) > 0 && parseFloat(row["win"]) > 0
                      ? `x${parseFloat(row["cashedOut"]).toFixed(2)}`
                      : "- -"
                  }}
                </span>
              </template>

              <template v-else-if="column.slug === 'type_id'">
                <span :class="`type-${row.type_id} type-of-bet`">
                  {{ formatTypeID(row) }}<br />
                </span>
              </template>

              <template v-else-if="column.formatter">
                <span
                  v-for="(item, index) in formatCashoutButton(row[column.slug])"
                  :key="index"
                  class="value type-badge text-center py-1 px-2 mx-1 text-capitalize"
                  :class="`${item.value}`"
                >
                  {{ item.label }}
                </span>
              </template>

              <template
                v-else-if="column.type === 'text' && column.isClickable"
              >
                <span
                  :title="
                    row[column.slug]
                      ? `Open ${column.label} ${row[column.slug]}`
                      : ''
                  "
                  :class="
                    !row[column.slug]
                      ? 'pointer-events-none'
                      : 'clickable-value'
                  "
                  @click="column.action(row)"
                >
                  {{ row[column.slug] ?? "- -" }}</span
                >
              </template>

              <template
                v-else-if="column.type === 'text' && column.slug === 'status'"
              >
                <span :class="getPlayerStatus(row[column.slug])">{{
                  row[column.slug]
                }}</span>
              </template>

              <template
                v-else-if="
                  column.type === 'indicator' && column.slug === 'status'
                "
              >
                <span :class="getFileStatus(row[column.slug])">{{
                  row[column.slug]
                }}</span>
              </template>

              <template
                v-else-if="column.type === 'text' && column.checkLength"
              >
                <span>{{ row[column.slug]?.length }}</span>
              </template>

              <template v-else-if="column.type === 'text'">
                {{ row[column.slug] }}
              </template>
              <template v-else-if="column.type === 'number'">
                <span
                  :class="
                    column.isMultiplierField
                      ? checkMultiplier(row[column.slug])
                      : ''
                  "
                  ><span v-if="column.isMultiplierField">x</span>
                  {{ parseFloat(row[column.slug]).toFixed(2) ?? "--" }}</span
                >
              </template>
              <template v-else-if="column.type === 'int'">
                <span>{{ parseInt(row[column.slug]) ?? "--" }}</span>
              </template>
              <template v-else-if="column.type === 'millisecond'">
                <span> {{ formatMillisecondToTime(row[column.slug]) }}</span>
              </template>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Paginazione -->
    <div class="my-2 d-flex justify-content-end align-items-center w-100 px-2">
      <ul
        class="d-flex justify-content-center align-items-center w-100 px-2 mb-0"
        v-if="totalPages > 1"
      >
        <li
          v-for="(page, index) in visiblePages"
          :key="index"
          class="change-page-btn text-white"
          :class="{
            'active-page': page === currentPage,
            'cursor-pointer': page !== '...',
            'cursor-not-allowed btn-off': page === '...',
          }"
          @click="changePagination(page)"
        >
          <button class=" ">
            {{ page }}
          </button>
        </li>
      </ul>
      <div class="limit-for-page">
        <select
          v-model="$parent.selectedLimit"
          @change="updateLimit(parseInt($event.target.value))"
          class="p-2 rounded-md"
        >
          <option
            v-for="option in limitOptions"
            :key="option.value"
            :value="option.value"
          >
            {{ option.label }}
          </option>
        </select>
      </div>
    </div>
  </div>
  <div class="loader-wrapper" v-else-if="isLoading">
    <Loader />
  </div>
  <div
    class="no-results-data text-center px-3"
    v-else-if="!isLoading && tableData.length === 0"
  >
    No results found for your search. Try using different keywords or filters.
    🔍
  </div>
</template>

<script>
import { formatString } from "@/helpers/date";
import Loader from "../Loader.vue";
import RainCalendar from "../RainComponent/RainCalendar.vue";
import { mapGetters } from "vuex";

export default {
  name: "Table",

  props: {
    tableData: {
      type: Array,
      required: true,
    },
    columns: {
      type: Array,
      required: true,
    },
    totalPages: {
      type: Number,
      required: true,
    },
    currentPage: {
      type: Number,
      required: true,
    },
    isLoading: {
      type: Boolean,
      required: false,
    },
    limitOptions: {
      type: Array,
      required: false,
    },
    totalElements: {
      type: Number,
      required: false,
    },
    allowMultipleSelection: {
      type: Boolean,
      required: false,
      default: false,
    },
    showRestoreButton: {
      type: Boolean,
      required: false,
      default: false,
    },
    sortHistory: {
      type: Array,
      required: false,
      default: () => [],
    },
    multipleSelection: Function,
    checkIsSelected: Function,
    openModalContent: Function,
    changePage: Function,
    removeRow: Function,
    unbanPlayer: Function,
    openRoundDetail: Function,
    openFreebetDetail: Function,
    removePlayerFromFreebet: Function,
    removePlayerFromCampaign: Function,
    restoreFreebets: Function,
    openCampaignID: Function,
    openRainDetails: Function,
    openPlayerDetail: Function,
    removeRainRow: Function,
    editRainRow: Function,
    openPlayer: Function,
    openGeolocationInfo: Function,
    downloadResource: Function,
    showExportDetail: Function,
    showTask: Function,
  },
  components: {
    Loader,
  },

  computed: {
    ...mapGetters(["multipleMultipliersGames", "getTypeID"]),
    visiblePages() {
      const pages = [];
      const range = 2;

      if (this.totalPages <= 5) {
        for (let i = 1; i <= this.totalPages; i++) {
          pages.push(i);
        }
      } else {
        pages.push(1);
        if (this.currentPage - range > 2) {
          pages.push("...");
        }

        for (
          let i = Math.max(2, this.currentPage - range);
          i <= Math.min(this.totalPages - 1, this.currentPage + range);
          i++
        ) {
          pages.push(i);
        }

        if (this.currentPage + range < this.totalPages - 1) {
          pages.push("...");
        }

        if (this.totalPages !== 1) {
          pages.push(this.totalPages);
        }
      }

      return pages;
    },
  },
  methods: {
    toggleSort(sortField, sortOrder) {
      this.$emit("sort-and-search", sortField, sortOrder);
    },
    isSortActive(field, direction) {
      if (!this.sortHistory) return false;
      return (
        this.sortHistory.field === field &&
        this.sortHistory.sorting === direction
      );
    },
    checkIfIsEnd(row) {
      if (!row || !row.end_at) return true;

      const endTime = new Date(row.end_at);
      if (isNaN(endTime.getTime())) return true;

      const now = new Date();
      let nowTime = now.getTime();

      return nowTime > endTime;
    },
    formatMillisecondToTime(ms) {
      if (!ms || ms < 0) return "0s";

      const seconds = Math.floor(ms / 1000);
      const minutes = Math.floor(seconds / 60);
      const hours = Math.floor(minutes / 60);
      const days = Math.floor(hours / 24);

      const remainingHours = hours % 24;
      const remainingMinutes = minutes % 60;
      const remainingSeconds = seconds % 60;

      let result = "";

      if (days > 0) result += `${days}d `;
      if (hours > 0 || days > 0) result += `${remainingHours}h `;
      if (minutes > 0 || hours > 0 || days > 0)
        result += `${remainingMinutes}m `;
      result += `${remainingSeconds}s`;

      return result.trim();
    },
    checkMultiplier(val) {
      let parsedVal = parseFloat(val);
      if (parsedVal < 1.41) {
        return "low-val val-multiplier";
      } else if (parsedVal > 1.4 && parsedVal < 2.81) {
        return "mid-val val-multiplier";
      } else {
        return "high-val val-multiplier";
      }
    },
    formatTypeID(row) {
      let db = this.$store?.getters?.selectedDatabase || "theearcasino";
      if (!this.multipleMultipliersGames?.includes(db)) {
        if (parseInt(row.type_id) <= 2) {
          return "Cashout" + " " + row.type_id;
        }

        if (parseInt(row.type_id) === 3) {
          return row.value;
        }

        if (parseInt(row.type_id) === 4) {
          return (
            "Ranges" +
            " " +
            (row.value == "0"
              ? "(1.00-1.40)"
              : row.value == "1"
              ? "(1.41-2.80)"
              : "(2.81+)")
          );
        }
      } else {
        // alert("qui");
        if (parseInt(row.type_id) <= 3) {
          return "Cashout" + " " + row.type_id;
        }

        if (parseInt(row.type_id) === 4) {
          return "Winner Race x(2.89)";
        }
      }
    },
    formatDate(dateString) {
      return formatString(dateString);
    },
    formatTime(timeString) {
      if (!timeString) return "-";

      return timeString;
    },
    handleButtonClick(row, buttonType) {
      const action = buttonType.action;
      if (this[action]) {
        this[action](row);
      }
    },
    changePagination(page) {
      if (page === "...") return;
      if (page === this.currentPage) return;
      if (page >= 1 && page <= this.totalPages) {
        this.changePage(page);
      }
    },
    updateLimit(val) {
      this.$parent.selectedLimit = val;
      this.changePage(1);
    },
    getPlayerStatus(status) {
      return `player-status ${status}`;
    },
    getFileStatus(status) {
      return `file-status ${status}`;
    },
    formatCashoutButton(val) {
      if (!val) return [];

      const validTokens = new Set([
        "cashout_1",
        "cashout_2",
        "oddeven",
        "ranges",
        "rain",
        "mission",
        "race",
        "tournament",
        "freebets",
        "freebets_boost",
        "freebets_lock",
        "freebets_final",
      ]);

      const labelMap = {
        cashout_1: "Cashout 1",
        cashout_2: "Cashout 2",
        oddeven: "Odd/Even",
        ranges: "Ranges",
        rain: "Rain",
        mission: "Mission",
        race: "Race",
        tournament: "Tournament",
        freebets: "Freebets",
        freebets_boost: "Freebets Boost",
        freebets_lock: "Freebets Lock",
        freebets_final: "Freebets Final",
      };

      const parts = val.replace(/\+/g, "_").split("_");

      const result = [];
      let i = 0;

      while (i < parts.length) {
        const one = parts[i];
        const two = parts[i + 1];

        const combined = one + "_" + two;

        if (validTokens.has(combined)) {
          result.push({
            value: combined,
            label: labelMap[combined],
          });
          i += 2;
        } else if (validTokens.has(one)) {
          result.push({
            value: one,
            label: labelMap[one],
          });
          i += 1;
        } else {
          // fallback: label grezza
          result.push({
            value: one,
            label: one,
          });
          i += 1;
        }
      }

      return result;
    },
  },
  watch: {
    tableData(newData) {},
  },
  mounted() {},
};
</script>

<style scoped>
ul {
  list-style-type: none;
  padding: 0;
  gap: 5px;
}

li {
  display: inline;
}
</style>

<style lang="scss" scoped>
table {
  width: 100%;
}

th,
td {
  text-align: left;
}

th {
  // background: rgb(15, 23, 42);
  flex: 1 1 auto;
  min-width: 100px;
  text-transform: uppercase;
  letter-spacing: -0.075em;
  font-size: 90%;
  font-weight: 600;
  color: #94a3b8;
  padding: 0.45rem 0.25rem;
  white-space: nowrap;
}

td {
  // border-bottom: 1px solid #e2e8f04d;
  flex: 1 1 auto;
  min-width: 100px;
  line-height: 1;
  font-size: 95%;
  padding: 0.95rem 0.25rem;
}

button {
  cursor: pointer;
  color: white;
}

.date-cell {
  letter-spacing: -0.065em;
}

.table-user_id {
  word-break: break-all;
  text-wrap: balance;
  word-wrap: break-word;
}

.cursor-not-allowed {
  cursor: not-allowed;
}

.btn-off {
  pointer-events: none;
}

.change-page-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0px 5px;
  border-radius: 8px;
  min-width: 30px;
  min-height: 30px;

  &:hover {
    cursor: pointer;
    background-color: #90a7cc;
    text-decoration: underline;
  }
}

.active-page {
  background-color: #667da3;
  color: white;
}

.loader-wrapper {
  min-height: 35vh;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
}

.no-results-data {
  min-height: 45vh;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  width: 100%;
  padding-top: 2.65rem;
}

.odd-row {
  background-color: #e9eaec;
}

.custom-icon-color {
  color: white;

  &:hover {
    color: greenyellow;
  }
}

.custom-icon-file-download {
  color: white;
  font-size: 1.25rem;

  &:hover {
    color: rgb(87, 221, 245);
  }
}

.trash-icon {
  color: rgb(236, 71, 71);

  &:hover {
    color: rgb(216, 50, 50);
  }
}

.unban-cta {
  color: rgb(162, 219, 76);
  text-transform: uppercase;
  font-weight: 500;

  &:hover {
    text-decoration: underline;
  }
}
</style>

<style lang="scss">
#table-td-action_column,
#table-th-action_column {
  text-align: center;
}

#table-td-game_id {
  text-transform: uppercase;
  letter-spacing: -0.045em;
}

.table-auto {
  font-size: 14px;
  border-collapse: separate;
  border-spacing: 0 8px;
  /* white-space: nowrap; */
  border: 0px solid transparent;
}

.table-auto > tbody > tr > td:first-child,
.table-auto > tbody > tr > th {
  border-left-style: solid;
  border-left-width: 1px;
  border-top-left-radius: 14px;
  border-bottom-left-radius: 14px;
  padding-left: 0.75rem;
  padding-right: 0.75rem;
}

.table-auto > tbody > tr td:last-child {
  border-right-style: solid;
  border-right-width: 1px;
  border-top-right-radius: 14px;
  border-bottom-right-radius: 14px;
}

.table-auto > tbody > tr td,
.table-auto > tbody > tr > th {
  vertical-align: middle;
  border: solid 1px #dbdfea3d;
  border-style: solid none;
  padding: 0.95rem 0.25rem;
  font-size: 0.875rem;
  /* min-width: 200px; */
  max-width: 400px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.multiple-selection {
  width: fit-content;
  max-width: fit-content;
  min-width: fit-content;
  display: inline-flex;
  padding: 0px 0.25rem 0px 0px;
  position: relative;
}
.win-value {
  color: #10b981;
  font-weight: 700;
}

.total-records {
  text-transform: uppercase;
  width: auto;
  max-width: fit-content;
  padding: 5px 16px;
  border-radius: 14px;
  font-size: 14px;
  border: 1px solid rgba(255, 255, 255, 0.288);
  margin-top: 0.55rem;

  b {
    color: #26ce80;
    margin: 0px 5px;
  }
}

.mission {
  background: linear-gradient(135deg, #a8aa18 0%, #767804 100%);
  color: white;
  border-radius: 90px;
  text-align: center;
}
.race {
  background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
  color: white;
  border-radius: 90px;
}

.tournament {
  background: linear-gradient(135deg, #ec6912 0%, #e97b31 100%);
  color: white;
  border-radius: 90px;
}

.rain {
  background: linear-gradient(135deg, #10b92c 0%, #047857 100%);
  color: white;
  border-radius: 90px;
  text-align: center;
}

.cashout_1,
.freebets {
  background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
  color: white;
  border-radius: 90px;
}

.cashout_2,
.freebets_boost {
  background: linear-gradient(135deg, #10b981 0%, #047857 100%);
  color: white;
  border-radius: 90px;
}

.oddeven,
.freebets_final {
  background: linear-gradient(135deg, #a8aa18 0%, #767804 100%);
  color: white;
  border-radius: 90px;
}

.all,
.freebets_lock {
  background: linear-gradient(135deg, #2576e0 0%, #4a2dcc 100% 100%);
  color: white;
  border-radius: 90px;
  min-width: 70px;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 26px;
  text-align: center;
}

.ranges {
  background: linear-gradient(135deg, #ec6912 0%, #e97b31 100%);
  color: white;
  border-radius: 90px;
}

.type-badge {
  color: white !important;
  text-transform: uppercase !important;

  &.low {
    background: linear-gradient(135deg, #10b92c 0%, #047857 100%);
    color: white !important;
    border-radius: 90px;
    text-transform: uppercase !important;
  }

  &.medium {
    background: linear-gradient(135deg, #b97e10 0%, #a55409 100%);
    color: white !important;
    border-radius: 90px;
    text-transform: uppercase !important;
  }

  &.high {
    background: linear-gradient(135deg, #aa0c33 0%, #a50620 100%);
    color: white !important;
    border-radius: 90px;
    text-transform: uppercase !important;
  }

  &.low_medium {
    background: linear-gradient(135deg, #10b981 0%, #be770b 100%);
    color: white !important;
    border-radius: 90px;
    text-transform: uppercase !important;
  }

  &.low_high {
    background: linear-gradient(135deg, #10b981 0%, #940725 100%);
    color: white !important;
    border-radius: 90px;
    text-transform: uppercase !important;
  }

  &.medium_high {
    background: linear-gradient(135deg, #b97e10 0%, #940725 100%);
    color: white !important;
    border-radius: 90px;
    text-transform: uppercase !important;
  }
}

.custom-checkbox {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: 1px solid white;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  top: 3px;
  left: -3px;
}

.checked {
  border: 1px solid #16cc95;
  &:after {
    content: "";
    position: absolute;
    background: #16cc95;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }
}

.clickable-value {
  cursor: pointer;
  transition: 0.3s all ease;

  &:hover {
    color: greenyellow;
    text-decoration: underline;
  }
}

.pointer-events-none {
  pointer-events: none;
  touch-action: none;
}

.z-95 {
  zoom: 95%;
}

.file-status {
  text-transform: uppercase;
}

.sort-arrow {
  font-size: 20px;
}

.sort-arrow.highlight {
  color: #e7ff5d;
  font-weight: 600;
  border: 1px solid #e7ff5d;
  border-radius: 50%;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>
